import React, { useState, useMemo, useEffect, useRef } from "react";
import { hasStagedChanges } from "../common/stagedChanges";
import { clearStagedDraft, getStagedDraft, setStagedDraft } from "../../utils/stagedDrafts";
import { KeycloakTokenParsed } from "keycloak-js";
import { toast } from 'react-toastify';

import { createProjectDailyUpdates, updateProjectDailyUpdates, deleteProjectDailyUpdates } from "../../api/dailyUpdates";
import { deleteDailyUpdateDocument } from "../../api/documents";
import { useProjectDailyUpdateStore, useProjectDailyUpdatePage } from "../../state/projects";

import { AddDailyUpdateModal } from './AddDailyUpdateModal';
import { LoadingSpinner } from '../LoadingSpinner';
import { DailyUpdatesDocumentAttachment } from './DailyUpdatesDocumentAttachment';

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaCalendarAlt,
  FaTrash
} from 'react-icons/fa';

import { 
  getTableHeaderStyle, 
  getFormLabelStyle, 
  getMultilineFieldStyle,
  getFormSelectContainerStyle,
  getFormSelectStyle,
  DesignSystem,
} from '../../styles/designSystem';

interface TabDailyUpdatesProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  readOnly?: boolean;
  dailyUpdates: ProjectDailyUpdate[];
  highlightedItemId?: string | null;
}

// Daily Update row component in ProjectPlan2 style
const DailyUpdateRow = ({
  dailyUpdate,
  projectData,
  onSubmit,
  onDelete,
  isNew = false,
  isHighlighted = false,
  readOnly = false
}: {
  dailyUpdate: ProjectDailyUpdate,
  projectData: ProjectInterface,
  onSubmit: (form: ProjectDailyUpdate) => Promise<void>,
  onDelete: (dailyUpdateId: string) => void,
  isNew?: boolean,
  isHighlighted?: boolean,
  readOnly?: boolean
}) => {
  // Unsaved edits survive a tab switch, which unmounts the row; see utils/stagedDrafts.
  const draftKey = `daily-update:${dailyUpdate.id}`;
  const [form, setForm] = useState<ProjectDailyUpdate>(() => getStagedDraft<ProjectDailyUpdate>(draftKey) ?? { ...dailyUpdate });
  // Last saved copy of the update. Edits are staged in `form` until Save; Cancel restores this copy.
  const [savedForm, setSavedForm] = useState({ ...dailyUpdate });
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [isSaving, setIsSaving] = useState(false);
  const rowRef = React.useRef<HTMLDivElement>(null);
  // Get store function to update single daily update
  const updateSingleDailyUpdate = useProjectDailyUpdateStore(state => state.updateSingleDailyUpdate);
  // Get reload function (like Documents tab does)
  const { loadProjectDailyUpdatesPage } = useProjectDailyUpdatePage(projectData?.id || '');

  // Function to count number of lines in text
  const calculateRows = (text: string, maxRows: number = 5) => {
    if (!text) return 1;
    const lines = text.split('\n').length;
    const estimatedWrappedLines = Math.ceil(text.length / 50); // Approximately 50 characters per line
    return Math.min(Math.max(lines, Math.ceil(estimatedWrappedLines)), maxRows);
  };

  // A reload of this update must not discard edits the user has staged.
  const savedFormRef = useRef(savedForm);
  savedFormRef.current = savedForm;
  useEffect(() => {
    setSavedForm({ ...dailyUpdate });
    setForm(prev => (hasStagedChanges(prev, savedFormRef.current) ? prev : { ...dailyUpdate }));
  }, [dailyUpdate]);

  // Scroll to highlighted item
  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isHighlighted]);

  // Function to save changes
  const saveChanges = async (updatedForm: ProjectDailyUpdate, reloadAfterSave = false) => {
    setIsSaving(true);
    try {
      await onSubmit(updatedForm);
      setSavedForm({ ...updatedForm });
      // Update the store so data persists across tab switches
      if (projectData?.id) {
        updateSingleDailyUpdate(projectData.id, updatedForm);
      }
      // Reload all daily updates from API (like Documents tab does)
      if (reloadAfterSave) {
        // Small delay to allow backend to process
        setTimeout(() => loadProjectDailyUpdatesPage(1), 500);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: string | number | boolean | null) => {
    // Only update local state; persistence happens on blur to reduce save noise
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Blur stages the value; a blur without a change does not send a request.
  const handleFieldBlur = (field: string, value: string | number | boolean | null) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Any staged difference from the saved copy means unsaved changes.
  const isDirty = hasStagedChanges(form, savedForm);

  const saveStagedChanges = () => saveChanges(form);

  const discardStagedChanges = () => {
    setForm({ ...savedForm });
  };

  // Keep the draft in step with the row: stored while edits are unsaved, cleared once they are saved or cancelled.
  useEffect(() => {
    if (isDirty) setStagedDraft(draftKey, form);
    else clearStagedDraft(draftKey);
  }, [isDirty, form, draftKey]);

  // Function to refresh daily update data after document operations
  // SCRUM-196: This is currently not used (onUpdate in DailyUpdatesDocumentAttachment disabled)
  // but kept for future use.
  const handleDailyUpdateUpdate = async () => {
    if (!projectData?.id) return;
    
    try {
      // Reload all daily updates (like Documents tab does)
      await loadProjectDailyUpdatesPage(1);
    } catch (error) {
      console.error('Failed to refresh daily update data:', error);
    }
  };

  return (
    <div 
      ref={rowRef}
      data-testid="daily-update-row"
      data-item-id={dailyUpdate.id}
      className={`group hover:bg-[#fafafa]/50 transition-all duration-200 ${
        isNew ? 'bg-[#f5f5f5]/30' : ''
      } ${
        isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
      } ${
        isDirty ? 'shadow-[inset_3px_0_0_#f59e0b]' : ''
      }`}
    >
      {isDirty && (
        <div data-testid="daily-update-unsaved-bar" className="flex items-center justify-end gap-2 px-6 py-1.5 bg-amber-50 border-b border-amber-100">
          <span className="text-[11px] text-amber-700 mr-auto">Unsaved changes</span>
          <button
            data-testid="daily-update-cancel"
            onClick={(e) => { e.stopPropagation(); discardStagedChanges(); }}
            disabled={isSaving}
            className="px-2 py-1 text-xs font-medium text-[#444] bg-white border border-[#eaeaea] rounded hover:bg-[#fafafa] transition-colors duration-150 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="daily-update-save"
            onClick={(e) => { e.stopPropagation(); saveStagedChanges(); }}
            disabled={isSaving}
            className="px-2 py-1 text-xs font-medium text-white bg-[#171717] rounded hover:bg-[#333] transition-colors duration-150 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
      {/* Main Row */}
      <div className="flex items-start px-6 py-4 relative" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-[#fafafa] transition-colors relative z-10 flex items-center"
        >
          <FaChevronDown className={`w-3 h-3 text-[#aaa] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Status Indicator */}
        <div className="mr-4 relative z-10 flex items-center space-x-2">
          <div className="relative">
            {/* Rotating Ring around status dot - when saving any field */}
            {isSaving && (
              <div className="absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin border-t-indigo-600/60 border-r-indigo-600/60" style={{ animation: 'spin 3s linear infinite' }} />
            )}
            {/* Status Dot */}
            <FaCircle 
              className="w-2 h-2 text-[#171717]"
            />
          </div>
        </div>

        {/* Date */}
        <div className="mx-3 w-24 flex items-center text-xs text-[#555] relative z-10">
          <input
            type="date"
            value={form.date ? new Date(form.date).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value ? new Date(e.target.value).toISOString() : '';
              handleFieldChange('date', value);
            }}
            onBlur={(e) => {
              const value = e.target.value ? new Date(e.target.value).toISOString() : '';
              handleFieldBlur('date', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="bg-transparent border-0 focus:outline-none text-xs w-full"
          />
        </div>

        {/* Location */}
        <div className="mx-3 w-24 relative z-10 flex items-start">
          <textarea
            value={form.location || ''}
            onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
            onBlur={(e) => handleFieldBlur('location', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none text-left resize-none"
            style={{ 
              fontSize: '13px',
              fontFamily: DesignSystem.typography.body.fontFamily,
              color: '#4B5563',
              lineHeight: '1.3'
            }}
            placeholder="Location"
            rows={3}
          />
        </div>

        {/* Resources Onsite */}
        <div className="mx-3 w-16 relative z-10 flex items-center">
          <input
            type="number"
            min="0"
            value={form.resources_onsite || ''}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : 0;
              handleFieldChange('resources_onsite', value);
            }}
            onBlur={(e) => {
              const value = e.target.value ? Number(e.target.value) : 0;
              handleFieldBlur('resources_onsite', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none text-sm text-center"
            placeholder="0"
          />
        </div>

        {/* What Done (Summary) */}
        <div className="flex-[2] min-w-0 mx-3 relative z-10 flex items-start">
          <textarea
            value={form.what_done || ''}
            onChange={(e) => setForm(prev => ({ ...prev, what_done: e.target.value }))}
            onBlur={(e) => handleFieldBlur('what_done', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-[#aaa] resize-none"
            style={getMultilineFieldStyle()}
            placeholder="What was accomplished today..."
            rows={calculateRows(form.what_done || '', 5)}
          />
        </div>

        {/* What is the goal for next day? */}
        <div className="flex-[1.5] min-w-0 mx-3 relative z-10 flex items-start">
          <textarea
            value={form.what_goal || ''}
            onChange={(e) => setForm(prev => ({ ...prev, what_goal: e.target.value }))}
            onBlur={(e) => handleFieldBlur('what_goal', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-[#aaa] resize-none"
            style={{ 
              fontSize: '12px',
              fontFamily: DesignSystem.typography.body.fontFamily,
              color: '#4B5563',
              lineHeight: '1.3'
            }}
            placeholder="What are the goals for next day..."
            rows={calculateRows(form.what_goal || '', 5)}
          />
        </div>

        {/* What are the road blocks? */}
        <div className="flex-[1.5] min-w-0 mx-3 relative z-10 flex items-start">
          <textarea
            value={form.what_blocks || ''}
            onChange={(e) => setForm(prev => ({ ...prev, what_blocks: e.target.value }))}
            onBlur={(e) => handleFieldBlur('what_blocks', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-[#aaa] resize-none"
            style={{ 
              fontSize: '12px',
              fontFamily: DesignSystem.typography.body.fontFamily,
              color: '#888',
              lineHeight: '1.3'
            }}
            placeholder="Any blockers or issues..."
            rows={calculateRows(form.what_blocks || '', 5)}
          />
        </div>

        {/* Documents */}
        <div className="ml-3 w-32 relative z-10">
          <div onClick={(e) => e.stopPropagation()}>
            <DailyUpdatesDocumentAttachment 
              projectId={projectData?.id || ''} 
              recordId={dailyUpdate.id}
              documentUrl={form.document || undefined}
              fileName={form.file_name || undefined}
              onUpdate={handleDailyUpdateUpdate}
              onDocumentChange={(docUrl, docName) => {
                // The document endpoint already stores the link, so no update request is needed.
                const documentFields = { document: docUrl || null, file_name: docName || null };
                setForm(prev => ({ ...prev, ...documentFields }));
                setSavedForm(prev => ({ ...prev, ...documentFields }));
              }}
            />
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2" style={{ 
          backgroundColor: DesignSystem.colors.background.gray50,
          borderTop: `0.5px solid ${DesignSystem.colors.border.gray200}` 
        }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {/* What was Done? - Full */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>What was Done?</label>
                <textarea
                  value={form.what_done || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, what_done: e.target.value }))}
                  className={`w-full bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 resize-none ${
                    (form.what_done || '') !== (dailyUpdate.what_done || '')
                      ? 'border-amber-400 focus:ring-amber-200 focus:border-amber-500'
                      : 'border-[#eaeaea] focus:ring-0 focus:border-[#888]'
                  }`}
                  placeholder="Describe what was accomplished today..."
                  rows={5}
                  style={getMultilineFieldStyle()}
                />
              </div>

              {/* What is the goal for next day? */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>What is the goal for next day?</label>
                <textarea
                  value={form.what_goal || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, what_goal: e.target.value }))}
                  className={`w-full bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 resize-none ${
                    (form.what_goal || '') !== (dailyUpdate.what_goal || '')
                      ? 'border-amber-400 focus:ring-amber-200 focus:border-amber-500'
                      : 'border-[#eaeaea] focus:ring-0 focus:border-[#888]'
                  }`}
                  placeholder="What are the goals for next day..."
                  rows={5}
                  style={{
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: '12px',
                    color: '#4B5563',
                    lineHeight: '1.3'
                  }}
                />
              </div>

              {/* What are the road blocks? */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>What are the road blocks?</label>
                <textarea
                  value={form.what_blocks || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, what_blocks: e.target.value }))}
                  className={`w-full bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 resize-none ${
                    (form.what_blocks || '') !== (dailyUpdate.what_blocks || '')
                      ? 'border-amber-400 focus:ring-amber-200 focus:border-amber-500'
                      : 'border-[#eaeaea] focus:ring-0 focus:border-[#888]'
                  }`}
                  placeholder="Any blockers or issues..."
                  rows={5}
                  style={{
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: '12px',
                    color: '#888',
                    lineHeight: '1.3'
                  }}
                />
              </div>

            </div>

            {/* Daily Update Details */}
            <div className="space-y-4">
              {/* Date */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Date</label>
                <input
                  type="date"
                  value={form.date ? new Date(form.date).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                    handleFieldChange('date', value);
                  }}
                  onBlur={(e) => {
                    const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                    handleFieldBlur('date', value);
                  }}
                  className="w-full text-sm text-[#444] bg-white border border-[#eaeaea] rounded-lg px-3 py-2 focus:outline-none focus:ring-0 focus:border-[#888]"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Location</label>
                <input
                  type="text"
                  value={form.location || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('location', e.target.value)}
                  className="w-full text-sm text-[#444] bg-white border border-[#eaeaea] rounded-lg px-3 py-2 focus:outline-none focus:ring-0 focus:border-[#888]"
                  placeholder="Enter location..."
                />
              </div>

              {/* # of Resource Onsite */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}># of Resource Onsite</label>
                <input
                  type="number"
                  min="0"
                  value={form.resources_onsite || ''}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : 0;
                    handleFieldChange('resources_onsite', value);
                  }}
                onBlur={(e) => {
                  const value = e.target.value ? Number(e.target.value) : 0;
                  handleFieldBlur('resources_onsite', value);
                }}
                  className="w-full text-sm text-[#444] bg-white border border-[#eaeaea] rounded-lg px-3 py-2 focus:outline-none focus:ring-0 focus:border-[#888]"
                  placeholder="0"
                />
              </div>

              {/* Actions */}
              <div className="pt-2">
                {!readOnly && (
                <button
                  onClick={() => onDelete(dailyUpdate.id!)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Update</span>
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const TabDailyUpdates = ({
  token,
  projectData,
  projectId,
  onProjectUpdate,
  dailyUpdates,
  highlightedItemId,
  readOnly,
}: TabDailyUpdatesProps) => {
  // Add a cleanup flag to prevent DOM operations after unmounting. Must be a ref,
  // not state: under React StrictMode's dev-only mount/cleanup/remount
  // simulation, a state-based flag set `true` by the cleanup never gets reset
  // back to `false`, permanently disabling every effect gated on it.
  const isUnmountedRef = useRef(false);
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showAddDailyUpdate, setShowAddDailyUpdate] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Scroll detection
  useEffect(() => {
    if (isUnmountedRef.current) return;

    const handleScroll = () => {
      if (!isUnmountedRef.current) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup effect to prevent DOM operations after unmount. Reset on setup
  // too, so StrictMode's mount/cleanup/remount simulation doesn't leave this
  // permanently `true` after the initial mount.
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (dailyUpdates && dailyUpdates.length >= 0) {
      setIsLoading(false);
    }
  }, [dailyUpdates]);

  // Listen for document deletion events from Documents tab
  useEffect(() => {
    console.log('🎧 [TabDailyUpdates] Setting up event listener for dailyUpdateDocumentDeleted');
    console.log('🎧 [TabDailyUpdates] Current projectId:', projectId);
    
    const handleDocumentDeleted = (event: CustomEvent) => {
      console.log('📢 [TabDailyUpdates] Received dailyUpdateDocumentDeleted event');
      console.log('📢 [TabDailyUpdates] Event detail:', event.detail);
      console.log('📢 [TabDailyUpdates] Current projectId:', projectId);
      console.log('📢 [TabDailyUpdates] Event projectId:', event.detail.projectId);
      
      if (projectId && event.detail.projectId === projectId) {
        console.log('🔄 [TabDailyUpdates] Project IDs match, will refresh after delay');
        // Add delay to allow backend to process the deletion and update relationships
        setTimeout(() => {
          console.log('🔄 [TabDailyUpdates] Refreshing daily updates now');
          if (onProjectUpdate) {
            onProjectUpdate();
          }
          console.log('✅ [TabDailyUpdates] Refresh triggered');
        }, 2000); // 2 second delay
      } else {
        console.log('⚠️ [TabDailyUpdates] Project IDs do not match or projectId is undefined');
      }
    };

    window.addEventListener('dailyUpdateDocumentDeleted', handleDocumentDeleted as EventListener);
    console.log('✅ [TabDailyUpdates] Event listener added');
    
    return () => {
      console.log('🧹 [TabDailyUpdates] Removing event listener');
      window.removeEventListener('dailyUpdateDocumentDeleted', handleDocumentDeleted as EventListener);
    };
  }, [projectId, onProjectUpdate]);

  const updateDailyUpdateHandler = async (form: ProjectDailyUpdate) => {
    try {
      if (token && projectId) {
        await updateProjectDailyUpdates(token, projectId, form.id, form);
        toast.success('Daily update updated successfully');
        
        // SCRUM-196 FIX: Don't call onProjectUpdate() after successful update
        // The local data is already up-to-date from the form
        // Calling onProjectUpdate() would reload all project data and overwrite 
        // any unsaved changes in other fields that the user is currently editing
        // This was causing data loss when user quickly edits multiple fields
        // Only reload on error (handled in catch block) or when adding/deleting items
      }
    } catch (error) {
      console.error('Error updating daily update:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update daily update');

      // SCRUM-196: On error, reload data to ensure consistency
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    }
  };

  const handleAddDailyUpdate = async (dailyUpdateData: ProjectDailyUpdate) => {
    try {
      if (token && projectId) {
        await createProjectDailyUpdates(token, projectId, dailyUpdateData);
        toast.success('Daily update added successfully');
        setShowAddDailyUpdate(false);

        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error) {
      console.error('Error adding daily update:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add daily update');
    }
  };

  const deleteDailyUpdateHandler = async (dailyUpdateId: string) => {
    if (!token || !projectId) return;

    if (!window.confirm('Are you sure you want to delete this daily update?')) return;

    try {
      // Find the daily update to check if it has a document
      const dailyUpdate = dailyUpdates.find(item => item.id === dailyUpdateId);

      // If daily update has a document, delete it first
      if (dailyUpdate?.document) {
        console.log(`Deleting document for daily update ${dailyUpdateId} before deleting the record itself`);
        try {
          await deleteDailyUpdateDocument(token, projectId, dailyUpdateId);
          console.log('Document deleted successfully');
        } catch (docError) {
          console.error('Failed to delete daily update document:', docError);
          // Continue with daily update deletion even if document deletion fails
          toast.warning('Daily update document could not be deleted, but will delete the record');
        }
      }

      // Delete the daily update itself
      await deleteProjectDailyUpdates(token, projectId, dailyUpdateId);
      toast.success('Daily update deleted successfully');

      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('Error deleting daily update:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete daily update');
    }
  };

  const getDateCount = (period: string) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);

    switch (period) {
      case 'today':
        return dailyUpdates.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate.toDateString() === today.toDateString();
        }).length;
      case 'yesterday':
        return dailyUpdates.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate.toDateString() === yesterday.toDateString();
        }).length;
      case 'this_week':
        return dailyUpdates.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate >= thisWeek;
        }).length;
      default:
        return 0;
    }
  };

  // Filter and search logic
  const filteredDailyUpdates = useMemo(() => {
    let filtered = dailyUpdates;

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.what_done?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.what_goal?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.what_blocks?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (dateFilter !== 'all') {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thisWeek = new Date(today);
      thisWeek.setDate(thisWeek.getDate() - 7);

      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        switch (dateFilter) {
          case 'today':
            return itemDate.toDateString() === today.toDateString();
          case 'yesterday':
            return itemDate.toDateString() === yesterday.toDateString();
          case 'this_week':
            return itemDate >= thisWeek;
          default:
            return true;
        }
      });
    }

    if (locationFilter !== 'all') {
      filtered = filtered.filter(item => item.location === locationFilter);
    }

    return filtered;
  }, [dailyUpdates, searchQuery, dateFilter, locationFilter]);

  // Get unique locations for filter
  const uniqueLocations = useMemo(() => {
    const locations = dailyUpdates.map(item => item.location).filter(Boolean);
    return [...new Set(locations)];
  }, [dailyUpdates]);

  return (
    <div className="min-h-screen">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div 
        className="sticky top-0"
        style={{ zIndex: 100 }}
      >
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_12px_rgba(0,0,0,0.06)]' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search daily updates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 w-80 text-[13px] text-[#444] bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 focus:bg-white transition-all duration-150 placeholder-[#aaa]"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-[#888]">
                      {filteredDailyUpdates.length} of {dailyUpdates.length} updates
                    </span>
                    <div className="w-px h-4 bg-[#ddd]"></div>
                    <span className="text-[#888]">
                      {getDateCount('today')} today
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters 
                        ? 'bg-[#171717] text-white border border-[#171717]'
                        : 'bg-white text-[#444] hover:bg-[#fafafa] border border-[#eaeaea]'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(dateFilter !== 'all' || locationFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white text-[#171717] rounded-full font-semibold">
                        {[dateFilter, locationFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* Add Daily Update */}
                    {!readOnly && (
                    <button
                      onClick={() => setShowAddDailyUpdate(true)}
                      className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] transition-colors duration-150"
                    >
                      <FaPlus className="w-3.5 h-3.5" />
                      <span>Add Daily Update</span>
                    </button>
                    )}
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #eaeaea' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All dates</option>
                        <option value="today">Today ({getDateCount('today')})</option>
                        <option value="yesterday">Yesterday ({getDateCount('yesterday')})</option>
                        <option value="this_week">This week ({getDateCount('this_week')})</option>
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All locations</option>
                        {uniqueLocations.map((location) => (
                          <option key={location} value={location}>
                            {location}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Active Filters & Clear */}
                  {(dateFilter !== 'all' || locationFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-[#888]">Active filters:</span>
                        {dateFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                            Date: {dateFilter.replace('_', ' ')}
                            <button onClick={() => setDateFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                        {locationFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                            Location: {locationFilter}
                            <button onClick={() => setLocationFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setDateFilter('all');
                          setLocationFilter('all');
                        }}
                        className="text-xs text-[#888] hover:text-[#444] font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="w-4 mr-4"></div> {/* Status indicator space */}
                <div className="mx-3 w-24" role="columnheader">Date</div>
                <div className="mx-3 w-24" role="columnheader">Location</div>
                <div className="mx-3 w-16" role="columnheader"># of Resource Onsite</div>
                <div className="flex-[2] mx-3" role="columnheader">What was Done?</div>
                <div className="flex-[1.5] mx-3" role="columnheader">What is the goal for next day?</div>
                <div className="flex-[1.5] mx-3" role="columnheader">What are the road blocks?</div>
                <div className="ml-3 w-32" role="columnheader">Docs</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Updates List - Scrollable Content */}
      <div
        className="max-w-[1280px] mx-auto px-0 -mt-4"
        style={{ position: 'relative', zIndex: 50 }}
      >
        {/* Loading State */}
        {isLoading && dailyUpdates.length === 0 && (
          <LoadingSpinner text="Loading daily updates..." />
        )}

        {!isLoading && (
        <div className="bg-white">
          {/* Daily Update Rows */}
          <div>
            {filteredDailyUpdates.sort((a: ProjectDailyUpdate, b: ProjectDailyUpdate) => {
              // Sort by date descending (newest first)
              return new Date(b.date).getTime() - new Date(a.date).getTime();
            }).map((dailyUpdate) => (
              <DailyUpdateRow
                key={dailyUpdate.id}
                dailyUpdate={dailyUpdate}
                projectData={projectData}
                onSubmit={updateDailyUpdateHandler}
                onDelete={deleteDailyUpdateHandler}
                isHighlighted={highlightedItemId === dailyUpdate.id}
                readOnly={readOnly}
              />
            ))}
          </div>

          {/* Empty State */}
          {!isLoading && filteredDailyUpdates.length === 0 && (
            <div className="text-center py-16">
              <div className="text-[#aaa] mb-4">
                <FaCalendarAlt className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-light text-[#171717] mb-2">No daily updates found</h3>
              <p className="text-[#555] font-light mb-8">
                {searchQuery || dateFilter !== 'all' || locationFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : readOnly ? 'No daily updates available.' : 'Get started by adding your first daily update.'
                }
              </p>
              {!readOnly && (
                <button
                  onClick={() => setShowAddDailyUpdate(true)}
                  className="bg-white border border-[#eaeaea] text-[#444] px-6 py-2 rounded-lg text-[13px] font-medium hover:bg-[#fafafa] hover:border-[#ddd] transition-colors duration-150"
                >
                  Add First Daily Update
                </button>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Add Daily Update Modal */}
      {showAddDailyUpdate && (
        <AddDailyUpdateModal
          isOpen={showAddDailyUpdate}
          onClose={() => setShowAddDailyUpdate(false)}
          onAddItem={handleAddDailyUpdate}
          projectData={projectData}
        />
      )}
    </div>
  );
}; 